import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { buildDataTableQueryOptions, ensureRouteQueriesData } from "@/lib/route-loader";
import { myClassesSearchSchema } from "@/lib/route-search";
import {
  classGetMyEnrolledClasses,
  classGetMyTeachingClasses,
  getClassGetMyEnrolledClassesQueryKey,
  getClassGetMyEnrolledClassesQueryOptions,
  getClassGetMyTeachingClassesQueryKey,
  getClassGetMyTeachingClassesQueryOptions,
} from "@/orval/class";
import type { MyEnrolledClassListItemDTO, MyTeachingClassListItemDTO } from "@/orval/model";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { format, parseISO } from "date-fns";

const myTeachingColumns: ColumnDef<MyTeachingClassListItemDTO>[] = [
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    accessorKey: "name",
  },
  {
    header: "Organization",
    accessorKey: "organizationName",
  },
  {
    header: "Date Range",
    accessorFn: (row) =>
      `${format(parseISO(row.startDate), "MM/dd/yyyy")} - ${format(parseISO(row.endDate), "MM/dd/yyyy")}`,
  },
  {
    header: "Enrolled Students",
    accessorKey: "enrolledStudentsCount",
  },
];

export const MyTeachingClassesTab = () => {
  return (
    <DataTable
      columns={myTeachingColumns}
      queryFn={classGetMyTeachingClasses}
      queryKeyFn={getClassGetMyTeachingClassesQueryKey}
      rowLink={(row) => ({ to: "/my/classes/$classId", params: { classId: row.id.toString() } })}
      getRowIdFn={(row) => row.id.toString()}
    />
  );
};

const myEnrolledColumns: ColumnDef<MyEnrolledClassListItemDTO>[] = [
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    accessorKey: "name",
  },
  {
    header: "Organization",
    accessorKey: "organizationName",
  },
  {
    header: "Date Range",
    accessorFn: (row) =>
      `${format(parseISO(row.startDate), "MM/dd/yyyy")} - ${format(parseISO(row.endDate), "MM/dd/yyyy")}`,
  },
];

export const MyEnrolledClassesTab = () => {
  return (
    <DataTable
      columns={myEnrolledColumns}
      queryFn={classGetMyEnrolledClasses}
      queryKeyFn={getClassGetMyEnrolledClassesQueryKey}
      rowLink={(row) => ({ to: "/my/classes/$classId", params: { classId: row.id.toString() } })}
      getRowIdFn={(row) => row.id.toString()}
    />
  );
};

const MyClassesPage = () => {
  const { tab } = Route.useSearch();
  const navigate = useNavigate();

  const onValueChange = (value: string) => {
    navigate({ to: ".", search: { tab: value } });
  };

  return (
    <div className="pt-8 pb-4 px-4 flex-1 min-h-0 flex flex-col container">
      <div className="flex flex-row items-end h-full">
        <div className="flex flex-col h-full">
          <Tabs defaultValue={tab} className="flex-1 h-full flex flex-col items-start" onValueChange={onValueChange}>
            <TabsList>
              <TabsTrigger value="teaching">Classes I'm Teaching</TabsTrigger>
              <TabsTrigger value="enrolled">Enrolled Classes</TabsTrigger>
            </TabsList>
            <TabsContent value="teaching" className="flex flex-col data-[state=inactive]:hidden flex-1 pt-2">
              <MyTeachingClassesTab />
            </TabsContent>
            <TabsContent value="enrolled" className="flex flex-col data-[state=inactive]:hidden flex-1 pt-2">
              <MyEnrolledClassesTab />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute("/_app/my/classes")({
  validateSearch: myClassesSearchSchema,
  loader: ({ location, ...loaderContext }) => {
    const { searchString } = myClassesSearchSchema.parse(location.search);
    const queryOptions = buildDataTableQueryOptions({ searchString });

    return ensureRouteQueriesData(loaderContext, [
      getClassGetMyTeachingClassesQueryOptions(queryOptions),
      getClassGetMyEnrolledClassesQueryOptions(queryOptions),
    ]);
  },
  component: MyClassesPage,
});
